import {zodResolver} from '@hookform/resolvers/zod';
import {
  type FieldPath,
  type Resolver,
  useForm,
  useFormContext,
} from 'react-hook-form';
import {z} from 'zod';
import {passwordFormat} from '@/components/utils/password-validation';

export const ipoKbnListWithoutCode = new Set([
  '0', // 未上場
  '99', // 海外市場
  '999', // -
]);

const listedCompanyCategoryId = '001';

const phoneNumberPattern = /^(0|8)[0-9]{1,3}(-[0-9]{1,4}){2}$/u;
const emailPattern = /.@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}$/;
const whiteSpacePattern = /^[ \u3000]*$/;

const errors = {
  required: '入力してください',
  selectionRequired: '選択してください',
  noIpoKbnForListingCompany: '上場企業の場合は上場区分を選択してください',
  length: (length: number) => `${length}文字で入力してください`,
  min: (min: number) => `${min}文字以上で入力してください`,
  max: (max: number) => `${max}文字以下で入力してください`,
  invalid: (field: string) => `正しい${field}を入力してください`,
  numberOnly: '半角数字のみで入力してください',
  katakanaOnly: '全角カタカナで入力してください',
  businessCategoryDuplicated: 'ビジネスカテゴリが重複しています',
  passwordInvalid: '満たされていない要件があります',
};

/**
 * 企業登録フォームのスキーマ
 * @see https://www.notion.so/fd9f28cd4a344ac7a4720420af6444ac?pvs=4
 * @todo [CRF-20] API での null と空文字の統一、整理 https://prtimes.atlassian.net/browse/CRF-20
 */
export const formSchema = z.object({
  /** 企業情報 */
  companyInfo: z
    .object({
      /** 法人名 */
      corporateName: z
        .string()
        .trim()
        .min(1, errors.required)
        .max(100, errors.max(100)),
      /** 個人事業主 */
      isSoleProprietor: z.boolean(),
      /** 法人名（カナ） */
      corporateNameKana: z
        .string()
        .trim()
        .min(1, errors.required)
        .regex(/^[ァ-ヶー]*$/, errors.katakanaOnly)
        .max(128, errors.max(128)),
      /** 代表者名 */
      presidentName: z
        .string()
        .trim()
        .min(1, errors.required)
        .max(40, errors.max(40)),
      /** 代表者役職 */
      presidentTitle: z
        .string()
        .trim()
        .min(1, errors.required)
        .max(64, errors.max(64)),
      /** 会社設立日 - 年 */
      foundationYear: z.union(
        [
          z
            .string()
            .trim()
            .regex(/^\d+$/, errors.numberOnly)
            .refine(
              (v) => v === '' || v.length === 4 || v.length === 3,
              errors.invalid('年'),
            )
            .refine(
              (v) => {
                if (v === '') {
                  return true;
                }

                const year = Number(v);
                return year >= 100 && year <= new Date().getFullYear();
              },
              {error: errors.invalid('年')},
            ),
          z.literal(''),
          z.string().regex(whiteSpacePattern),
        ],
        {
          error(issue) {
            return issue.errors[0][0].message;
          },
        },
      ),
      /** 会社設立日 - 月 */
      foundationMonth: z.union(
        [
          z
            .string()
            .trim()
            .regex(/^\d+$/, errors.numberOnly)
            .regex(/^(0?[1-9]|1[0-2])$/, errors.invalid('月')),
          z.literal(''),
          z.string().regex(whiteSpacePattern),
        ],
        {
          error(issue) {
            return issue.errors[0][0].message;
          },
        },
      ),
      /** 本社所在地 - 郵便番号 */
      zip: z
        .string()
        .trim()
        .min(1, errors.required)
        .regex(/^\d+$/, errors.numberOnly)
        .length(7, errors.length(7)),
      /** 本社所在地 - 住所（市町村 / 番地） */
      address: z
        .string()
        .trim()
        .min(1, errors.required)
        .max(200, errors.max(200)),
      /** 本社所在地 - 住所（建物名 / 階数） */
      address2: z.string().trim().max(100, errors.max(100)),
      /** 代表電話番号 */
      phoneNumber: z.union(
        [
          z
            .string()
            .trim()
            .regex(/^[\d-]+$/, errors.numberOnly)
            .regex(phoneNumberPattern, errors.invalid('電話番号'))
            // ハイフンを含めて13文字までしか入力できない
            .max(13, errors.invalid('電話番号')),
          z.literal(''),
        ],
        {
          error(issue) {
            return issue.errors[0][0].message;
          },
        },
      ),
      /** 上場区分 */
      ipoKbn: z.string().min(1, errors.selectionRequired),
      /** 証券コード 英数字の4桁 */
      securityCode: z.union(
        [
          z
            .string()
            .trim()
            .regex(/^[a-zA-Z\d]+$/, '半角英数字のみで入力してください')
            .min(4, errors.length(4))
            .max(4, errors.length(4)),
          z.literal(''),
          z.string().regex(whiteSpacePattern),
        ],
        {
          error(issue) {
            return issue.errors[0][0].message;
          },
        },
      ),
      /** 資本金（万円） */
      capital: z.preprocess(
        (v) => (typeof v === 'string' && v.trim() === '' ? '' : v),
        z.union(
          [
            z
              .string()
              .trim()
              .regex(/^\d+$/, errors.numberOnly)
              .max(9, '9桁以下で入力してください')
              .pipe(z.string().transform(Number))
              .refine((v) => v >= 0, {error: errors.invalid('資本金')}),
            z.literal(''),
            z.number(), // 一度確認ステップに進んだ後に再度入力ステップに戻った際に値がnumber型になるため、number型も許容する
          ],
          {
            error(issue) {
              return issue.errors[0][0].message;
            },
          },
        ),
      ),
      /** 社員数 */
      employees: z.preprocess(
        (v) => (typeof v === 'string' && v.trim() === '' ? '' : v),
        z.union(
          [
            z
              .string()
              .trim()
              .regex(/^\d+$/, errors.numberOnly)
              .max(8, '8桁以下で入力してください')
              .pipe(z.string().transform(Number))
              .refine((v) => v >= 0, {error: errors.invalid('社員数')}),
            z.literal(''),
            z.number(), // 一度確認ステップに進んだ後に再度入力ステップに戻った際に値がnumber型になるため、number型も許容する
          ],
          {
            error(issue) {
              return issue.errors[0][0].message;
            },
          },
        ),
      ),
      /** サイトURL */
      websiteUrl: z.union(
        [
          z
            .string()
            .trim()
            .max(240, errors.max(240))
            .pipe(z.url({error: errors.invalid('URL')})),
          z.literal(''),
          z.string().regex(whiteSpacePattern),
        ],
        {
          error(issue) {
            return issue.errors[0][0].message;
          },
        },
      ),
      /** 業種区分ID */
      industryKbnId: z.string().min(1, errors.selectionRequired),
      /** 企業カテゴリ */
      companyCategoryIds: z.array(z.string().min(1, errors.selectionRequired)),
      /** ビジネスカテゴリ */
      businessCategoryLv2Ids: z
        .array(z.string())
        .length(3)
        .refine((v) => v.some((v) => v !== ''), errors.selectionRequired)
        .refine((v) => {
          const nonEmpties = v.filter((v) => v !== '');
          return new Set(nonEmpties).size === nonEmpties.length; // 同じカテゴリが複数回選択されていないこと
        }, errors.businessCategoryDuplicated),
    })
    // 上場企業の場合は証券コードが必須
    .refine(
      (v) => {
        if (v.ipoKbn === '' || ipoKbnListWithoutCode.has(v.ipoKbn)) {
          return true;
        }

        return v.securityCode.trim() !== '';
      },
      {
        error: errors.required,
        path: ['securityCode'],
      },
    )
    // 企業カテゴリが上場企業の場合は上場区分で未上場、-を選択できない
    .refine(
      (v) => {
        if (!v.companyCategoryIds.includes(listedCompanyCategoryId)) {
          return true;
        }

        return !new Set(['0', '999']).has(v.ipoKbn);
      },
      {error: '上場企業の場合は上場区分を選択してください', path: ['ipoKbn']},
    )
    // 年月が未来日でないこと
    .refine(
      (v) => {
        const {foundationYear, foundationMonth} = v;
        if (
          (foundationYear.trim() !== '' && foundationMonth.trim() === '') ||
          (foundationYear.trim() === '' && foundationMonth.trim() !== '')
        ) {
          return true;
        }

        const year = Number(foundationYear);
        const month = Number(foundationMonth);
        const date = new Date(year, month - 1, 1);
        const today = new Date();
        return date <= today;
      },
      {error: errors.invalid('年月'), path: ['foundationYear']},
    )
    // foundationMonthが入力されていて、foundationYearが未入力の場合はエラー
    .refine(
      (v) => {
        const {foundationYear, foundationMonth} = v;
        if (foundationMonth.trim() === '' || foundationYear.trim() !== '') {
          return true;
        }

        return false;
      },
      {error: errors.required, path: ['foundationYear']},
    )
    // foundationYearが入力されていて、foundationMonthが未入力の場合はエラー
    .refine(
      (v) => {
        const {foundationYear, foundationMonth} = v;
        if (foundationYear.trim() === '' || foundationMonth.trim() !== '') {
          return true;
        }

        return false;
      },
      {error: errors.required, path: ['foundationMonth']},
    ),
  /** 担当者情報 */
  contactPersonInfo: z
    .object({
      /** メールアドレス */
      email: z
        .string()
        .trim()
        .min(1, errors.required)
        .regex(emailPattern, errors.invalid('メールアドレス'))
        .max(100, errors.max(100)),
      /** パスワード */
      password: z
        .string()
        .trim()
        .min(1, errors.required)
        .regex(passwordFormat, errors.passwordInvalid),
      /** パスワード確認 */
      passwordConfirmation: z.string().trim().min(1, errors.required),
      /** 所属部署 */
      department: z.union([
        z.string().min(1).max(100, errors.max(100)),
        z.literal(''),
      ]),
      /** 氏名 - 姓 */
      lastName: z
        .string()
        .trim()
        .min(1, errors.required)
        .max(20, errors.max(20)),
      /** 氏名 - 名 */
      firstName: z
        .string()
        .trim()
        .min(1, errors.required)
        .max(20, errors.max(20)),
      /** 氏名 - 姓（カナ） */
      lastNameKana: z
        .string()
        .trim()
        .min(1, errors.required)
        .regex(/^[ァ-ヶー]*$/, errors.katakanaOnly)
        .max(20, errors.max(20)),
      /** 氏名 - 名（カナ） */
      firstNameKana: z
        .string()
        .trim()
        .min(1, errors.required)
        .regex(/^[ァ-ヶー]*$/, errors.katakanaOnly)
        .max(20, errors.max(20)),
      /** 電話番号 */
      phoneNumber: z
        .string()
        .trim()
        .min(1, errors.required)
        .regex(/^[\d-]+$/, errors.numberOnly)
        .regex(phoneNumberPattern, errors.invalid('電話番号'))
        // ハイフンを含めて13文字までしか入力できない
        .max(13, errors.invalid('電話番号')),
      /** FAX */
      fax: z.union([
        z
          .string()
          .trim()
          .regex(/^[\d-]+$/, errors.numberOnly)
          .regex(phoneNumberPattern, errors.invalid('FAX番号'))
          // ハイフンを含めて13文字までしか入力できない
          .max(13, errors.invalid('FAX番号')),
        z.literal(''),
      ]),
    })
    .refine((v) => v.password === v.passwordConfirmation, {
      path: ['passwordConfirmation'],
      error: '一致しません',
    }),
  /** 請求情報 */
  billingInfo: z
    .object({
      /** 会社名 */
      companyName: z
        .string()
        .trim()
        .min(1, errors.required)
        .max(100, errors.max(100)),
      /** 請求先住所 - 郵便番号 */
      zip: z
        .string()
        .trim()
        .min(1, errors.required)
        .regex(/^\d+$/, errors.numberOnly)
        .length(7, errors.length(7))
        .regex(/^\d{7}$/, errors.numberOnly),
      /** 請求先住所 - 住所（都道府県 / 市町村 / 番地） */
      address: z
        .string()
        .trim()
        .min(1, errors.required)
        .max(200, errors.max(200)),
      /** 請求先住所 - 住所（建物名 / 階数） */
      address2: z.string().trim().max(100, errors.max(100)),
      /** 所属部署 */
      department: z.string().trim().max(100, errors.max(100)),
      /** 氏名 - 姓 */
      lastName: z
        .string()
        .trim()
        .min(1, errors.required)
        .max(20, errors.max(20)),
      /** 氏名 - 名 */
      firstName: z
        .string()
        .trim()
        .min(1, errors.required)
        .max(20, errors.max(20)),
      /** 氏名 - 姓（カナ） */
      lastNameKana: z
        .string()
        .trim()
        .min(1, errors.required)
        .regex(/^[ァ-ヶー]*$/, errors.katakanaOnly)
        .max(20, errors.max(20)),
      /** 氏名 - 名（カナ） */
      firstNameKana: z
        .string()
        .trim()
        .min(1, errors.required)
        .regex(/^[ァ-ヶー]*$/, errors.katakanaOnly)
        .max(20, errors.max(20)),
      /** メールアドレス */
      email: z
        .string()
        .trim()
        .min(1, errors.required)
        .regex(emailPattern, errors.invalid('メールアドレス'))
        .max(100, errors.max(100)),
      /** メールアドレス（cc） */
      emailCc: z.union(
        [
          z
            .string()
            .trim()
            .regex(emailPattern, errors.invalid('メールアドレス'))
            .max(100, errors.max(100)),
          z.literal(''),
          z.string().regex(whiteSpacePattern),
        ],
        {
          error(issue) {
            return issue.errors[0][0].message;
          },
        },
      ),
    })
    .refine(
      (v) => {
        if (v.emailCc === '') {
          return true;
        }

        return v.email !== v.emailCc;
      },
      {
        error: 'メールアドレスと異なるメールアドレスを入力してください',
        path: ['emailCc'],
      },
    )
    .refine(
      (v) => {
        if (v.zip === '0000000') {
          return true;
        }

        return v.address.trim().length > 0;
      },
      {error: errors.required, path: ['address']},
    ),
  /** 確認情報 */
  confirmInfo: z.object({
    /** 利用規約同意 */
    agreeToTerms: z.boolean().refine((v) => v, errors.selectionRequired),
  }),
});

export type FormSchema = z.infer<typeof formSchema>;

export type FormFieldPath = FieldPath<FormSchema>;

export const defaultValues = {
  companyInfo: {
    corporateName: '',
    isSoleProprietor: false,
    corporateNameKana: '',
    presidentName: '',
    presidentTitle: '',
    foundationYear: '',
    foundationMonth: '',
    zip: '',
    address: '',
    address2: '',
    phoneNumber: '',
    ipoKbn: '',
    securityCode: '',
    capital: '',
    employees: '',
    websiteUrl: '',
    industryKbnId: '',
    companyCategoryIds: [],
    businessCategoryLv2Ids: ['', '', ''],
  },
  contactPersonInfo: {
    email: '',
    password: '',
    passwordConfirmation: '',
    department: '',
    lastName: '',
    firstName: '',
    lastNameKana: '',
    firstNameKana: '',
    phoneNumber: '',
    fax: '',
  },
  billingInfo: {
    companyName: '',
    zip: '',
    address: '',
    address2: '',
    department: '',
    lastName: '',
    firstName: '',
    lastNameKana: '',
    firstNameKana: '',
    email: '',
    emailCc: '',
  },
  confirmInfo: {
    agreeToTerms: false,
  },
} satisfies FormSchema;

export const useCompanyRegistrationForm = (defaultValues: FormSchema) =>
  useForm<FormSchema>({
    resolver: zodResolver(formSchema) as Resolver<FormSchema>,
    defaultValues,
  });

export const useCompanyRegistrationFormContext = () =>
  useFormContext<FormSchema>();
